import { useEffect, useState } from "react";
import { api } from "../../api/api";
import { formatarNomeUpper, aplicarMascaraCPF} from "../../utils/formatadores"

// Função para calcular os dias transcorridos e determinar a cor da categoria
const obterStatusDias = (dataIso) => {
  if (!dataIso) return { texto: "-", cor: "#6b7280" };

  const dataAtendimento = new Date(dataIso);
  const hoje = new Date();

  dataAtendimento.setHours(0, 0, 0, 0);
  hoje.setHours(0, 0, 0, 0);

  const diferencaEmMs = hoje.getTime() - dataAtendimento.getTime();
  const dias = Math.floor(diferencaEmMs / (1000 * 60 * 60 * 24));

  // Formatação do Texto
  let texto = `${dias} dias`;
  if (dias === 0) texto = "Hoje";
  if (dias === 1) texto = "1 dia";

  // Categoria de Cores
  let cor = "#16a34a"; // Verde (<= 10 dias)

  if (dias >= 11 && dias <= 15) {
    cor = "#d97706"; // Amarelo/Laranja (11 a 15 dias)
  } else if (dias > 15) {
    cor = "#dc2626"; // Vermelho (> 15 dias)
  }

  return { texto, cor, dias };
};

const Pendentes = () => {
  const [atendimentos, setAtendimentos] = useState([]);
  const [proximosPassos, setProximosPassos] = useState({});
  const [confirmandoId, setConfirmandoId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  // Carrega os atendimentos que precisam ser analisados
  useEffect(() => {
    const carregar = async () => {
      try {
        setCarregando(true);
        setErro("");

        const response = await api.get("/atendimento/pendentes");

        const dados = Array.isArray(response.data)
          ? response.data
          : [];

        const filtrados = dados.filter(
          (atendimento) =>
            atendimento.proximoPasso === "analisar"
        );

        setAtendimentos(filtrados);
      } catch (err) {
        console.error("Erro ao carregar atendimentos:", err);
        setErro("Não foi possível carregar os atendimentos.");
      } finally {
        setCarregando(false);
      }
    };

    carregar();
  }, []);

  // Atualiza somente a seleção do atendimento correspondente
  const handleProximoPasso = (id, valor) => {
    setProximosPassos((prev) => ({
      ...prev,
      [id]: valor,
    }));

    // Se o usuário mudar a opção durante a confirmação,
    // exige uma nova confirmação.
    if (confirmandoId === id) {
      setConfirmandoId(null);
    }
  };

  // Finaliza individualmente o atendimento selecionado
  const handlePendente = async (atendimento) => {
    const id = atendimento._id || atendimento.id;

    if (!id) {
      setErro("Não foi possível identificar o atendimento.");
      return;
    }

    const proximoPassoSelecionado = proximosPassos[id];

    if (
      !["peticionar", "recusado", "concluido"].includes(
        proximoPassoSelecionado
      )
    ) {
      setErro("Selecione um desfecho antes de finalizar.");
      return;
    }

    // Primeiro clique: solicita confirmação
    if (confirmandoId !== id) {
      setConfirmandoId(id);
      setErro("");
      return;
    }

    try {
      setLoadingId(id);
      setErro("");

      // Envia o desfecho do atendimento específico
      await api.put(
        `/atendimento/${id}/atualizar-atendimento`,
        {
          proximoPasso: proximoPassoSelecionado,
        }
      );

      // Remove somente o registro atualizado
      setAtendimentos((prev) =>
        prev.filter(
          (item) => (item._id || item.id) !== id
        )
      );

      // Limpa somente a seleção desse registro
      setProximosPassos((prev) => {
        const atualizado = { ...prev };
        delete atualizado[id];
        return atualizado;
      });

      setConfirmandoId(null);
    } catch (err) {
      console.error(
        "Erro ao atualizar o próximo passo:",
        err
      );

      setErro(
        err.response?.data?.message ||
          "Não foi possível atualizar o atendimento."
      );
    } finally {
      setLoadingId(null);
    }
  };

  const formatarData = (data) => {
    if (!data) return "-";

    const dataConvertida = new Date(data);

    if (Number.isNaN(dataConvertida.getTime())) {
      return "-";
    }

    return dataConvertida.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
    });
  };

  return (
    <div className="container-consulta-processos">
      <div className="card">
        <h1>ANÁLISES PENDENTES</h1>

        {erro && (
          <div
            role="alert"
            style={{
              padding: "10px",
              marginBottom: "12px",
              color: "#991b1b",
              backgroundColor: "#fee2e2",
              borderRadius: "4px",
            }}
          >
            {erro}
          </div>
        )}

        <div className="table-container">
          {carregando ? (
            <p>Carregando atendimentos...</p>
          ) : atendimentos.length === 0 ? (
            <p>Nenhum cliente com status "analisar".</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CPF</th>
                  <th>Data do atendimento</th>
                  <th>Descrição</th>
                  <th>Status</th>
                  <th>Dias</th>
                  <th>Desfecho</th>
                  <th>Ação</th>
                </tr>
              </thead>

              <tbody>
                {atendimentos.map((atendimento) => {
                  const id =
                    atendimento._id || atendimento.id;

                  const cliente = atendimento.cliente || {};
                  const passoSelecionado = proximosPassos[id];
                  const carregandoEste = loadingId === id;
                  const confirmandoEste = confirmandoId === id;
                  // Obtém o texto e a cor correspondente aos dias decorridos
                  const statusDias = obterStatusDias(atendimento.horarioCheckin);

                  return (
                    <tr key={id}>
                      <td>
                        {formatarNomeUpper(cliente.nomeCliente) || "-"}
                      </td>
                      <td style={{ textAlign: "center" }}>{aplicarMascaraCPF(cliente.cpf) || "-"}</td>
                      <td style={{ textAlign: "center" }}>{formatarData(atendimento.horarioCheckin)}</td>
                      <td>{atendimento.descricao}</td>
                      <td style={{ textAlign: "center" }}>{atendimento.proximoPasso}</td>
                      {/* RANKING DE DIAS COM COR DINÂMICA */}
                      <td
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          color: statusDias.cor,
                        }}
                      >
                        {statusDias.texto}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <select
                          name={`proximoPasso-${id}`}
                          value={passoSelecionado || ""}
                          onChange={(e) =>
                            handleProximoPasso(
                              id,
                              e.target.value
                            )
                          }
                          disabled={carregandoEste}
                          required
                          aria-label={`Desfecho de ${
                            cliente.nomeCliente || "atendimento"
                          }`}
                        >
                          <option value="">
                            Selecione...
                          </option>

                          <option value="peticionar">
                            Peticionar
                          </option>

                          <option value="recusado">
                            Recusado
                          </option>
                          <option value="concluido">
                            Concluído
                          </option>
                        </select>
                      </td>

                      <td style={{ textAlign: "center" }}>
                        {confirmandoEste ? (
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                handlePendente(atendimento)
                              }
                              disabled={
                                carregandoEste ||
                                !passoSelecionado
                              }
                            >
                              {carregandoEste
                                ? "Salvando..."
                                : "Confirmar"}
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                setConfirmandoId(null)
                              }
                              disabled={carregandoEste}
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              handlePendente(atendimento)
                            }
                            disabled={
                              carregandoEste ||
                              !passoSelecionado
                            }
                            style={{
                              backgroundColor:
                                !passoSelecionado
                                  ? "#9ca3af"
                                  : "#2563eb",
                              color: "#fff",
                              padding: "6px 10px",
                              border: "none",
                              borderRadius: "4px",
                              cursor: !passoSelecionado
                                ? "not-allowed"
                                : "pointer",
                            }}
                          >
                            Finalizar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pendentes;